/**
 * "Group these for me" — the classification studio's AI panel.
 *
 * The studio had no AI at all: 400 unsorted photos meant 400 drags, while the
 * detection studio next door had a whole auto-label panel. This is that missing
 * half, driving the clustering endpoint that already existed.
 *
 * Two deliberate choices in the wording:
 *
 * - **"Find the groups for me" is the primary action**, not a number. The
 *   server picks k by silhouette score, and its answer is usually better than a
 *   guess — offering "3 / 4 / 5" first guarantees the "I asked for 5 and got 4"
 *   disappointment, because KMeans can leave a centroid unused.
 * - **It says what it will touch before it touches it.** Grouping moves images
 *   out of the unlabeled pool, which is the one thing here a user cannot undo
 *   with a single click.
 */
import React, { useState } from 'react';
import { useI18n } from '../../../../../i18n';
import useAutoGroup from '../hooks/useAutoGroup';
import { MAX_GROUPING_SAMPLE, MIN_GROUPING_IMAGES } from '../utils/autoGroup';

const COUNT_CHOICES = [0, 2, 3, 4, 5, 6];

const AutoGroupPanel = ({ onClose }) => {
  const { t } = useI18n();
  const group = useAutoGroup();
  const [count, setCount] = useState(0); // 0 = let the server decide

  const capped = group.unlabeledCount > MAX_GROUPING_SAMPLE;
  const willGroup = Math.min(group.unlabeledCount, MAX_GROUPING_SAMPLE);

  return (
    <div className="auto-group-panel">
      <div className="auto-group-panel__header">
        <span className="auto-group-panel__title">
          <i className="fa fa-object-group" aria-hidden="true" /> {t('autoGroup.title')}
        </span>
        <button
          type="button"
          className="auto-group-panel__close"
          onClick={onClose}
          title={t('common.close')}
          aria-label={t('common.close')}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <div className="auto-group-panel__body">
        {!group.canGroup ? (
          <p className="auto-group-panel__hint">
            {t('autoGroup.notEnough', { min: MIN_GROUPING_IMAGES })}
          </p>
        ) : (
          <>
            <p className="auto-group-panel__lead">
              {t('autoGroup.lead', { count: willGroup })}
            </p>
            {capped && (
              <p className="auto-group-panel__hint">
                {t('autoGroup.capped', {
                  total: group.unlabeledCount,
                  count: willGroup,
                  rest: group.unlabeledCount - willGroup,
                })}
              </p>
            )}

            <span className="auto-group-panel__label">{t('autoGroup.howMany')}</span>
            <div className="auto-group-panel__choices">
              {COUNT_CHOICES.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`auto-group-panel__choice${count === value ? ' is-on' : ''}`}
                  onClick={() => setCount(value)}
                  disabled={group.running}
                >
                  {value === 0 ? t('autoGroup.auto') : value}
                </button>
              ))}
            </div>

            {group.running && (
              <div className="auto-group-panel__progress" role="status">
                <span>
                  {group.phase === 'reading'
                    ? t('autoGroup.reading', { done: group.done, total: group.total })
                    : t('autoGroup.grouping')}
                </span>
                <span className="auto-group-panel__bar">
                  <span
                    className="auto-group-panel__fill"
                    style={{
                      width: `${group.total ? Math.round((group.done / group.total) * 100) : 10}%`,
                    }}
                  />
                </span>
              </div>
            )}

            {group.error && (
              <p className="auto-group-panel__error" role="alert">
                {group.error === 'notEnough'
                  ? t('autoGroup.notEnough', { min: MIN_GROUPING_IMAGES })
                  : group.error}
              </p>
            )}

            {group.result && (
              <p className="auto-group-panel__result">
                <i className="fa fa-check-circle" aria-hidden="true" />{' '}
                {t('autoGroup.result', {
                  grouped: group.result.grouped,
                  groups: group.result.groups,
                })}
                {group.result.untouched > 0 &&
                  ` ${t('autoGroup.resultRest', { count: group.result.untouched })}`}
              </p>
            )}

            <div className="auto-group-panel__actions">
              {group.running ? (
                <button type="button" className="auto-group-panel__stop" onClick={group.cancel}>
                  <i className="fa fa-stop" aria-hidden="true" /> {t('autoGroup.stop')}
                </button>
              ) : (
                <button
                  type="button"
                  className="auto-group-panel__run"
                  onClick={() => group.run({ numClusters: count || null })}
                >
                  <i className="fa fa-magic" aria-hidden="true" /> {t('autoGroup.run')}
                </button>
              )}
            </div>
            <p className="auto-group-panel__hint">{t('autoGroup.footnote')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AutoGroupPanel;
